import React from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const SecuritySettings = ({ settings, onChange, errors }) => {
  const expirationOptions = [
    { value: '1', label: '1 Hour', description: 'Quick review session' },
    { value: '6', label: '6 Hours', description: 'Same day review' },
    { value: '24', label: '24 Hours', description: 'Standard timeframe' },
    { value: '72', label: '3 Days', description: 'Extended review period' },
    { value: '168', label: '1 Week', description: 'Maximum security period' },
    { value: 'custom', label: 'Custom', description: 'Set your own timeframe' }
  ];

  const customTypeOptions = [
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' }
  ];

  const handleSettingChange = (key, value) => {
    onChange({ ...settings, [key]: value });
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 elevation-1">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
          <Icon name="Shield" size={20} color="var(--color-accent)" />
        </div>
        <div>
          <h3 className="text-lg font-heading text-foreground">Security Preferences</h3>
          <p className="text-sm text-muted-foreground">Configure protection and access controls</p>
        </div>
      </div>
      <div className="space-y-6">
        {/* Screenshot Prevention */}
        <div className="flex items-start space-x-4">
          <Checkbox
            checked={settings?.screenshotPrevention}
            onChange={(e) => handleSettingChange('screenshotPrevention', e?.target?.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <label className="text-sm font-medium text-foreground">Screenshot Prevention</label>
              <div className="px-2 py-1 bg-accent/10 text-accent text-xs rounded-full font-medium">
                Recommended
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Prevents clients from taking screenshots or screen recordings of your designs
            </p>
          </div>
        </div>

        {/* Watermark Protection */}
        <div className="flex items-start space-x-4">
          <Checkbox
            checked={settings?.watermarkProtection}
            onChange={(e) => handleSettingChange('watermarkProtection', e?.target?.checked)}
            className="mt-1"
          />
          <div className="flex-1">
            <label className="text-sm font-medium text-foreground mb-1 block">Watermark Protection</label>
            <p className="text-sm text-muted-foreground">
              Add subtle watermarks to protect your intellectual property
            </p>
          </div>
        </div>

        {/* Link Expiration */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">Link Expiration Time</label>
          <Select
            options={expirationOptions}
            value={settings?.expirationTime}
            onChange={(value) => handleSettingChange('expirationTime', value)}
            placeholder="Select expiration time"
            error={errors?.expirationTime}
            className="w-full"
          />
          
          {/* Custom expiration settings */}
          {settings?.expirationTime === 'custom' && (
            <div className="p-4 bg-muted/50 rounded-lg space-y-3">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground mb-2 block">Expiration Value</label>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={settings?.customExpirationValue || 1}
                    onChange={(e) => handleSettingChange('customExpirationValue', parseInt(e?.target?.value))}
                    className="w-full p-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent text-foreground bg-background"
                    placeholder="Enter number"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground mb-2 block">Time Unit</label>
                  <Select
                    options={customTypeOptions}
                    value={settings?.customExpirationType || 'hours'}
                    onChange={(value) => handleSettingChange('customExpirationType', value)}
                    placeholder="Select unit"
                    className="w-full"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Preview link will expire after {settings?.customExpirationValue || 1} {settings?.customExpirationType || 'hours'}
              </p>
            </div>
          )}
          
          <p className="text-sm text-muted-foreground">
            Preview link will automatically expire after the selected duration
          </p>
        </div>

        {/* Access Restrictions */}
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">Access Restrictions</label>
          
          <div className="space-y-3">
            <div className="flex items-start space-x-4">
              <Checkbox
                checked={settings?.singleUseLink}
                onChange={(e) => handleSettingChange('singleUseLink', e?.target?.checked)}
                className="mt-1"
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-1 block">Single-Use Link</label>
                <p className="text-sm text-muted-foreground">
                  Link becomes invalid after first access (maximum security)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Security Level Indicator */}
        <div className="p-4 bg-muted/50 rounded-lg">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-success/10 rounded-full flex items-center justify-center">
              <Icon name="ShieldCheck" size={16} color="var(--color-success)" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">High Security Level</p>
              <p className="text-xs text-muted-foreground">
                Your current settings provide excellent protection for sensitive designs
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;